/**
 * The one guarded way to close query tabs (Codex r5).
 *
 * Closing a tab with an open transaction — or one whose BEGIN is still on the
 * wire — must ask first and, on confirm, commit or roll back and release the
 * session before the tab goes. Only the single-tab close used to: Close Others,
 * Close to the Right and Close All removed such tabs outright, leaving their
 * sessions (and locks) pinned with no tab to end them. Every tab-removal path
 * now goes through `requestTabsClose`: it closes at once when none of the
 * tabs is guarded, otherwise it opens the transaction close dialog
 * (`Tabs.tsx`) for the guarded ones and runs the close after the user decides.
 *
 * Not routed here, because the transaction is ended elsewhere: a disconnect
 * (its own confirmation, then the connection teardown rolls every session on
 * it back — `releaseTransactionSessionsForConnection`), and window close / quit
 * (main rolls every session back and disposes pending BEGINs; the close
 * handshake is bounded to 3 s by design, so it cannot wait on a prompt).
 */
import { create } from 'zustand';
import { useWorkspaceStore } from './workspaceStore';
import { isQueryTab, type Tab } from '../types/workspaceTypes';
import {
  commitTransaction,
  rollbackTransaction,
  TRANSACTION_SETTLE_IN_PROGRESS_CODE,
} from '../../query-editor/services/transactionApi';
import {
  TRANSACTION_TAB_IDLE_RESET,
  transactionEndedByFailedSettle,
} from '../../query-editor/hooks/useTransactionSessionsReleased';
import { cancelTabTransactionBegin } from '../../query-editor/services/tabTransactionBegin';
import { toast } from '../../../../components/ui/toaster';
import { confirmDiscardStoragePreviews, discardStoragePreviewEdits } from '../../storage-explorer/preview/storagePreviewOpenClose';

/**
 * The close itself, run once the transactions are ended. `kept` are the tabs
 * whose Commit / Rollback failed: they stay open (with their transaction
 * state), so the close must leave them.
 */
export type TabClosePerform = (kept: ReadonlySet<string>) => void;

export type TabCloseRequest = {
  /** The guarded tabs among those being closed. */
  tabIds: string[];
  /** Whether every guarded tab is only STARTING a transaction (nothing to commit yet). */
  startingOnly: boolean;
  perform: TabClosePerform;
  /**
   * A session switch / new session closes EVERYTHING or nothing: if a tab has
   * to stay open, the transition does not happen (the other tabs' transactions
   * are still ended, as the user chose).
   */
  allOrNothing: boolean;
};

/** A tab kept open because its transaction could not be ended. */
export type KeptTab = { tabId: string; title: string; reason: string };

type TabCloseGuardState = {
  request: TabCloseRequest | null;
  open: (request: TabCloseRequest) => void;
  clear: () => void;
};

export const useTabCloseGuardStore = create<TabCloseGuardState>()((set) => ({
  request: null,
  open: (request) => set({ request }),
  clear: () => set({ request: null }),
}));

type GuardKind = 'open' | 'starting';

function guardOf(tab: Tab | undefined): GuardKind | null {
  if (!tab || !isQueryTab(tab)) return null;
  if (tab.content.transactionState === 'active' || tab.content.transactionState === 'error') return 'open';
  if (tab.content.transactionBeginToken !== undefined) return 'starting';
  return null;
}

/**
 * Close `tabIds` via `perform` — at once when none holds an open or starting
 * transaction, otherwise after the user confirms in the transaction close
 * dialog (which ends those transactions first).
 */
export function requestTabsClose(
  tabIds: readonly string[],
  perform: TabClosePerform,
  options: { allOrNothing?: boolean } = {},
): void {
  // Storage tabs with an unsaved edit in their file preview ask first (every bulk close and session switch passes here).
  // Agreeing discards nothing yet: the edits go only with the tabs that really close, after the transaction guard below
  // (cancelling that dialog, or an all-or-nothing switch that keeps a tab, leaves the edits in place).
  confirmDiscardStoragePreviews(tabIds, () => requestTransactionTabsClose(tabIds, (kept) => {
    discardStoragePreviewEdits(tabIds.filter((id) => !kept.has(id)));
    perform(kept);
  }, options));
}

function requestTransactionTabsClose(
  tabIds: readonly string[],
  perform: TabClosePerform,
  options: { allOrNothing?: boolean },
): void {
  const { getTab } = useWorkspaceStore.getState();
  const guarded = tabIds
    .map((id) => ({ id, kind: guardOf(getTab(id)) }))
    .filter((entry): entry is { id: string; kind: GuardKind } => entry.kind !== null);
  if (guarded.length === 0) {
    perform(new Set());
    return;
  }
  useTabCloseGuardStore.getState().open({
    tabIds: guarded.map((entry) => entry.id),
    startingOnly: guarded.every((entry) => entry.kind === 'starting'),
    perform,
    allOrNothing: options.allOrNothing ?? false,
  });
}

/**
 * What the dialog's Commit / Rollback does for each guarded tab: settle, CHECK
 * the outcome (Codex r6) and make the tab say what the backend now holds
 * (Codex r7). The backend drops the session with the settle, so there is
 * nothing left to release. A settled tab goes idle. A tab whose COMMIT or
 * ROLLBACK failed is kept open and reported; unless the other op was still
 * settling it (`SETTLE_IN_PROGRESS`), the backend has already ended that
 * session (rolled back or its connection destroyed), so the tab goes idle too
 * and keeps the error in its messages — a phantom `active` blocked every later
 * close and session switch. All of it applies only while the tab still holds
 * the session this settle was for (Codex r8): a late answer never resets a
 * NEWER transaction, and a tab that began one meanwhile stays open. A tab
 * whose BEGIN is still on the wire has it abandoned (main ends the late
 * session).
 */
export async function endTransactionsForClose(
  tabIds: readonly string[],
  action: 'commit' | 'rollback',
): Promise<KeptTab[]> {
  const kept: KeptTab[] = [];
  const { getTab, updateTabContent } = useWorkspaceStore.getState();
  const generationOf = (tab: Tab | undefined) => (tab && isQueryTab(tab) ? tab.content.transactionSessionGeneration : undefined);
  await Promise.all(tabIds.map(async (tabId) => {
    if (cancelTabTransactionBegin(tabId)) return;
    const settledGeneration = generationOf(getTab(tabId));
    const result = await (action === 'commit' ? commitTransaction(tabId) : rollbackTransaction(tabId));
    const tab = getTab(tabId);
    // The toolbar's rule (`settlementOwnsTab`): the tab still holds the settled session.
    const ownsTab = !tab || generationOf(tab) === settledGeneration;
    const title = tab?.title ?? tabId;
    if (!ownsTab) {
      const outcome = result.success ? (action === 'commit' ? 'committed' : 'rolled back') : `failed: ${result.error ?? 'unknown error'}`;
      kept.push({ tabId, title, reason: `Its earlier ${action} ${outcome}; the tab has started a new transaction since, which was left open.` });
      return;
    }
    if (result.success) {
      if (tab) updateTabContent(tabId, TRANSACTION_TAB_IDLE_RESET);
      return;
    }
    const reason = `${action === 'commit' ? 'Commit' : 'Rollback'} failed: ${result.error ?? 'unknown error'}`;
    kept.push({ tabId, title, reason });
    if (!tab || !isQueryTab(tab) || result.code === TRANSACTION_SETTLE_IN_PROGRESS_CODE) return;
    updateTabContent(tabId, transactionEndedByFailedSettle(tab.content, reason));
  }));
  return kept;
}

/**
 * The Commit / Rollback and Close decision, carried out: end every guarded
 * tab's transaction, close what may close, and say what was kept open and why
 * — one toast naming the tab, or one summary for several.
 */
export async function completeTabsClose(request: TabCloseRequest, action: 'commit' | 'rollback'): Promise<void> {
  const kept = await endTransactionsForClose(request.tabIds, action);
  const keptIds = new Set(kept.map((entry) => entry.tabId));
  const performed = !(request.allOrNothing && kept.length > 0);
  if (performed) request.perform(keptIds);
  if (kept.length === 0) return;
  const lines = kept.map((entry) => `"${entry.title}": ${entry.reason}`).join('\n');
  toast.error({
    title: !performed
      ? `Not switched — ${kept.length === 1 ? `"${kept[0].title}" was` : `${kept.length} tabs were`} kept open`
      : kept.length === 1
        ? `"${kept[0].title}" was kept open`
        : `${kept.length} tabs were kept open`,
    description: lines,
  });
}
